import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { HardHat, Building2, X, Droplets, Route } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import MobileCraneIcon from './icons/MobileCraneIcon';
import ExcavatorIcon from './icons/ExcavatorIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INDUSTRY_SHOWN_KEY = 'equipr_industry_shown';

const industries = [
  {
    id: 'cranes',
    title: 'Cranes & Rigging',
    description: 'Mobile cranes, tower cranes, rigging crews',
    icon: MobileCraneIcon,
    path: '/mobile-crane-hire-rigging',
    borderColor: 'border-yellow-500/30',
    iconBg: 'bg-yellow-500/20',
    iconColor: 'text-yellow-500',
    bgImage: 'https://equiprwebsitestorage.blob.core.windows.net/website-assets/brochures/borger-cranes-rar-resized.jpg',
  },
  {
    id: 'plant',
    title: 'Operated Plant Hire',
    description: 'Excavators, earthmoving, operated equipment',
    icon: ExcavatorIcon,
    path: '/plant-hire-software',
    borderColor: 'border-blue-500/30',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-500',
    bgImage: 'https://images.unsplash.com/photo-1613928270169-4e4462c75a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxleGNhdmF0b3IlMjBlYXJ0aG1vdmluZyUyMHBsYW50JTIwaGlyZSUyMHllbGxvdyUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3NzMyMDE1NzR8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'civil',
    title: 'Civil Construction',
    description: 'Civil contractors, infrastructure projects',
    icon: Building2,
    path: '/civil-construction-operations-software',
    borderColor: 'border-green-500/30',
    iconBg: 'bg-green-500/20',
    iconColor: 'text-green-500',
    bgImage: 'https://images.pexels.com/photos/159306/construction-site-build-construction-work-159306.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'concrete',
    title: 'Concrete Pumping',
    description: 'Concrete pumping fleets, volume tracking, pour scheduling',
    icon: Droplets,
    path: '/concrete-pumping-software',
    borderColor: 'border-orange-500/30',
    iconBg: 'bg-orange-500/20',
    iconColor: 'text-orange-500',
    bgImage: 'https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'sweeping',
    title: 'Road Sweeping',
    description: 'Route planning, GPS tracking, recurring job management',
    icon: Route,
    path: '/road-sweeping-software',
    borderColor: 'border-teal-500/30',
    iconBg: 'bg-teal-500/20',
    iconColor: 'text-teal-500',
    bgImage: 'https://images.unsplash.com/photo-1764981966665-1abf644d10a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzdHJlZXQlMjBzd2VlcGVyJTIwbXVuaWNpcGFsJTIwY2xlYW5pbmclMjB2ZWhpY2xlfGVufDB8fHx8MTc3MzIwMDEyN3ww&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'other',
    title: 'Other Industry',
    description: 'Transport, logistics, other equipment-based operations',
    icon: HardHat,
    path: null, // Stay on home page
    borderColor: 'border-gray-500/30',
    iconBg: 'bg-gray-500/20',
    iconColor: 'text-gray-400',
    bgImage: 'https://images.pexels.com/photos/14367421/pexels-photo-14367421.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
];

const IndustrySelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only show on home page and if not shown before
    if (location.pathname === '/') {
      const hasShown = sessionStorage.getItem(INDUSTRY_SHOWN_KEY);
      if (!hasShown) {
        // Small delay for better UX
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname]);

  // Track industry selection
  const trackSelection = async (industryId) => {
    try {
      await axios.post(`${API}/analytics/industry`, null, {
        params: {
          industry: industryId,
          page_path: location.pathname
        }
      });
    } catch (error) {
      console.log('Analytics tracking failed silently');
    }
  };

  const handleSelect = (industry) => {
    sessionStorage.setItem(INDUSTRY_SHOWN_KEY, 'true');
    trackSelection(industry.id);
    setIsOpen(false);
    
    if (industry.path) {
      navigate(industry.path);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem(INDUSTRY_SHOWN_KEY, 'true');
    trackSelection('skip');
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
            onClick={handleSkip}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center z-[101] p-4"
          >
            <div 
              className="bg-equipr-dark border border-white/10 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 pb-4 border-b border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase">
                    Welcome to EQUIPR
                  </h2>
                  <button
                    onClick={handleSkip}
                    className="text-white/40 hover:text-white transition-colors p-1"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-white/60 text-sm md:text-base">
                  Tell us about your industry so we can show you the most relevant information.
                </p>
              </div>

              {/* Industry Options */}
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {industries.map((industry) => (
                  <motion.button
                    key={industry.id}
                    onClick={() => handleSelect(industry)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-5 rounded-lg border ${industry.borderColor} text-left transition-all hover:border-equipr-yellow/50 group overflow-hidden min-h-[140px]`}
                    data-testid={`industry-${industry.id}`}
                  >
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                      style={{ backgroundImage: `url(${industry.bgImage})` }}
                    />
                    {/* Dark Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/50 group-hover:from-black/80 group-hover:via-black/60 transition-all" />
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className={`w-12 h-12 ${industry.iconBg} backdrop-blur-sm rounded-lg flex items-center justify-center mb-3`}>
                        <industry.icon className={`w-6 h-6 ${industry.iconColor}`} />
                      </div>
                      <h3 className="font-heading text-lg font-bold text-white uppercase mb-1 group-hover:text-equipr-yellow transition-colors">
                        {industry.title}
                      </h3>
                      <p className="text-white/70 text-sm">
                        {industry.description}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Footer */}
              <div className="p-6 pt-2 border-t border-white/5">
                <Button
                  onClick={handleSkip}
                  variant="ghost"
                  className="w-full text-white/50 hover:text-white hover:bg-white/5"
                  data-testid="industry-skip"
                >
                  Skip for now — I'll explore on my own
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default IndustrySelector;
